/**
 * English catalog — a full translation of the externalised tranche.
 *
 * Declared `: TranslationKeys`, so TypeScript enforces exact structural parity
 * with the French source (`fr.ts`): a missing key or an unknown extra key fails
 * the build. Any string not yet externalised elsewhere in the app still falls
 * back to French at runtime (see FALLBACK_LOCALE / useT).
 */

import type { TranslationKeys } from './fr';

const en: TranslationKeys = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    create: 'Create',
    edit: 'Edit',
    search: 'Search',
    loading: 'Loading…',
    confirm: 'Confirm',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    add: 'Add',
    remove: 'Remove',
    apply: 'Apply',
    reset: 'Reset',
    refresh: 'Refresh',
    retry: 'Retry',
    duplicate: 'Duplicate',
    export: 'Export',
    import: 'Import',
    copy: 'Copy',
    download: 'Download',
    upload: 'Upload',
    filter: 'Filter',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    actions: 'Actions',
    more: 'More',
    settings: 'Settings',
    processing: 'Processing…',
    readOnly: 'Read-only',
    empty: 'No items',
  },

  nav: {
    brand: 'PubSocialNet',
    groups: {
      general: 'General',
      creation: 'Creation',
      prospection: 'Prospecting',
      ai: 'AI',
      system: 'System',
    },
    items: {
      dashboard: 'Dashboard',
      content: 'Content',
      calendar: 'Calendar',
      publishing: 'Publishing',
      contentQueue: 'Content Queue',
      bulkSchedule: 'Bulk Scheduling',
      platforms: 'Platforms',
      ads: 'Ads',
      mediaLibrary: 'Media library',
      analytics: 'Analytics',
      design: 'Design',
      workflows: 'Workflows',
      crm: 'CRM & Leads',
      discovery: 'Discovery',
      prospectionRecommend: 'Recommendations',
      prospectionCalendar: 'Prospection calendar',
      accountIntelligence: 'Account intelligence',
      carouselEditor: 'Carousel editor',
      richContentStudio: 'Rich content studio',
      prospect: 'B2B database',
      marketplace: 'Marketplace',
      social: 'Social leads',
      outreach: 'Email outreach',
      inbox: 'Social inbox',
      community: 'Community',
      listening: 'Social listening',
      bio: 'Link-in-bio',
      projects: 'Projects',
      aiTeam: 'AI team',
      aiOffice: 'AI office',
      aiTeams: 'AI teams',
      aiDeliverables: 'AI deliverables',
      aiSchedules: 'AI schedules',
      aiControl: 'AI control',
      aiAuditTrail: 'AI audit trail',
      staffQuestions: 'Agent questions',
      grants: 'Grant requests',
      superadminFleet: 'Platform fleet',
      orchestration: 'AI orchestration',
      aiAssistant: 'AI assistant',
      contentStudio: 'Content studio',
      ghostwriter: 'AI ghostwriter',
      repurpose: 'Repurposing',
      hookLibrary: 'Hook library',
      brandVoice: 'Brand voice',
      abCopy: 'A/B copy tests',
      suggestions: 'AI suggestions',
      personalization: 'Personalization',
      communityAi: 'Community AI',
      ragDocuments: 'RAG documents',
      memory: 'AI memory',
      approvals: 'Approvals',
      aiActivity: 'AI activity',
      settings: 'Settings',
    },
    header: {
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      toggleSidebar: 'Collapse or expand the sidebar',
      breadcrumbRoot: 'PubSocialNet',
      breadcrumbAria: 'Breadcrumb',
      searchPlaceholder: 'Search…',
      openCommandPalette: 'Open the command palette',
      quickActions: 'Quick actions',
      create: 'Create',
      createItem: 'Create an item',
      shortcuts: 'Keyboard shortcuts',
      showShortcuts: 'Show keyboard shortcuts',
    },
    quickCreate: {
      newContent: 'New content',
      newContact: 'New contact',
      newCompany: 'New company',
      newCampaign: 'New campaign',
      newProject: 'New project',
      newDesign: 'New design',
    },
    quickAccess: {
      search: 'Search (⌘K)',
      newContent: 'New content',
      newContact: 'New contact',
      newDeal: 'New deal',
      inbox: 'Social inbox',
      assistant: 'AI assistant',
    },
    user: {
      fallbackName: 'User',
      connectedAccount: 'Connected account',
      settings: 'Settings',
      signout: 'Sign out',
    },
    footer: {
      environment: 'Runtime environment',
      whatsNew: "What's new",
      shortcuts: 'Shortcuts',
      privacy: 'Privacy',
      links: 'Footer links',
      statusTitle: 'Service status (/health polled every 30 s)',
      operational: 'Operational',
      unavailable: 'Service unavailable',
      checking: 'Checking…',
    },
    primaryNav: 'Primary navigation',
  },

  settings: {
    title: 'Settings',
    subtitle:
      'AI credits & billing, agent and model registry, team, profile and social connections.',
    navAria: 'Settings navigation',
    tabs: {
      credits: 'Credits & Billing',
      subscription: 'Subscription',
      referral: 'Referral',
      aiRegistry: 'AI registry',
      governance: 'AI governance',
      connectors: 'Connectors',
      developers: 'Developer API',
      integrations: 'Integrations',
      connections: 'Connected accounts',
      team: 'Team',
      profile: 'Profile',
    },
  },

  profile: {
    tabsAria: 'Profile sections',
    tabProfile: 'Profile',
    tabConnections: 'Connections',
    emailVerified: 'Email verified',
    emailUnverified: 'Email not verified',
    noRole: 'no role',
    stats: {
      roles: 'Roles',
      session: 'Session',
      expiresAt: 'expires at',
    },
    account: {
      title: 'Account',
      name: 'Name',
      email: 'Email',
      username: 'Username',
      tenant: 'Tenant',
      userId: 'User ID',
      roles: 'Roles',
    },
    prefs: {
      title: 'Preferences',
      language: 'Language',
      languageHint: 'Preference remembered on this device.',
      theme: 'Theme',
      themeHint: '“System” follows the browser’s light/dark setting.',
      themeSystem: 'System',
      themeLight: 'Light',
      themeDark: 'Dark',
      appearance: 'Appearance',
      appearanceHint: 'Light/dark toggle and accent color — also available from the header.',
    },
  },

  platforms: {
    connections: {
      needsReconnectBadge: 'Reconnect required',
      needsReconnectHint:
        'The stored token can no longer be read on the server — this connection can no longer publish. Reconnect the account to fix it.',
      reconnectAction: 'Reconnect',
    },
  },
};

export default en;
